import React, { useState, useEffect, useMemo } from 'react';
import call_center from '../../assets/call-center.svg';
import explore from '../../assets/explore.svg';
import inspiration from '../../assets/inspiration.svg';
import travel from '../../assets/travel.svg';

import { Swiper, SwiperSlide } from 'swiper/react';
import 'swiper/css';
import 'swiper/css/pagination';
import { Autoplay, Pagination } from 'swiper/modules';

// Import all necessary utilities, including the base URL
import { 
    fetchDomesticPackages, 
    fetchInternationalPackages,
    API_BASE_URL // <-- Import API Base URL
} from '../utils/api'; 

// Utility to shuffle an array
const shuffleArray = (array) => {
  let currentIndex = array.length, randomIndex;

  while (currentIndex !== 0) {
    randomIndex = Math.floor(Math.random() * currentIndex);
    currentIndex--;

    [array[currentIndex], array[randomIndex]] = [
      array[randomIndex], array[currentIndex]];
  }
  return array;
};

// Skeleton Loader Component
const CardSkeleton = () => (
  <div className='relative overflow-hidden rounded-lg animate-pulse'>
    <div className='h-44 w-full rounded-lg bg-gray-300 sm:h-40 md:h-48'></div>
    <div className='mt-2 h-4 w-3/4 mx-auto bg-gray-300 rounded'></div>
  </div>
);

const SpecialPicks = () => {
  const [offers, setOffers] = useState([]);
  const [isLoading, setIsLoading] = useState(true);

  const todayKey = useMemo(() => {
    return new Date().toISOString().slice(0, 10);
  }, []);

  useEffect(() => {
    const loadPackages = async () => {
      setIsLoading(true); 
      
      try {
        const [domesticData, internationalData] = await Promise.all([
          fetchDomesticPackages(),
          fetchInternationalPackages(),
        ]);

        // 1. Combine and Process Data
        let allPackages = [...domesticData, ...internationalData];
        
        // Map the raw data to the structure expected by the component (img, text)
        // This is the CRITICAL change to fix the image path.
        const processedPackages = allPackages.map(pkg => ({
            // CRITICAL: Construct the full, absolute URL
            img: pkg.image ? `${API_BASE_URL}/${pkg.image}` : null,
            // Use the title from the package as the text/description
            text: pkg.title || 'Special Offer Package',
            // Keep other properties if needed
            ...pkg,
        }));

        // 2. Shuffle for randomization
        const shuffledPackages = shuffleArray(processedPackages);

        // 3. Select a maximum of 10 with images
        const finalPicks = shuffledPackages
          .filter(pkg => pkg.img) 
          .slice(0, 10);
        
        setOffers(finalPicks);
      } catch (error) {
        console.error("Failed to load packages:", error);
        setOffers([]); 
      } finally {
        setIsLoading(false); 
      }
    };

    loadPackages();
  }, [todayKey]);

  const cards2 = [
    { name: 'Inspiring Journey', img: 'inspiration' },
    { name: 'Travel Helpdesk', img: 'call_center' },
    { name: 'Explore', img: 'explore' },
    { name: 'Pack & Go', img: 'travel' },
  ];

  // Determine what to display in the Swiper
  const swiperContent = isLoading
    ? Array.from({ length: 4 }).map((_, index) => (
        <SwiperSlide key={`skeleton-${index}`}>
          <CardSkeleton />
        </SwiperSlide>
      ))
    : offers.map((offer, idx) => (
        <SwiperSlide key={idx}>
          <div className='relative overflow-hidden rounded-lg'>
            <img
              // This 'offer.img' now holds the full absolute URL (e.g., http://localhost:4000/uploads/...)
              src={offer.img} 
              alt={offer.text || 'Special offer image'}
              className='h-44 w-full rounded-lg object-cover sm:h-40 md:h-48'
              // Add onError just in case the image still fails to load
              onError={(e) => { e.target.onerror = null; e.target.style.display = 'none'; }}
            />
          </div>
        </SwiperSlide>
      ));

  return (
    <>
      {/* Section with polygon borders (omitted for brevity) */}
      <div className='px-0 sm:px-16'>
        <div className='relative'>
          <section className='polygon-bottom relative min-h-[420px] bg-gradient-to-r from-[#a3aaff] to-[#baf7e1] p-4 sm:p-8 md:min-h-[470px] md:p-10 lg:p-12 xl:p-14'>
            <div className='mx-auto max-w-7xl'>
              {/* heading + subtext (omitted for brevity) */}
              <div className='mx-auto max-w-7xl'>
                <div className='mb-6 flex flex-col sm:mb-8 sm:flex-row-reverse sm:items-end sm:justify-between'>
                  <p className='Orbitron text-center text-3xl font-medium leading-tight tracking-normal text-[#495BFF] md:text-start md:text-[40px]'>
                    Special Picks
                  </p>
                  <p className='mt-2 text-center text-lg font-semibold text-[#3a7a9e] sm:mt-0 sm:max-w-none md:text-left'>
                    View all live offers for your Holiday packages bookings
                  </p>
                </div>
              </div>
              {/* Swiper Slider */}
              <Swiper
                modules={[Autoplay, Pagination]}
                autoplay={!isLoading ? { delay: 5000, disableOnInteraction: false } : false} 
                pagination={{
                  clickable: true,
                  renderBullet: (index, className) =>
                    `<span class="${className} custom-bullet"></span>`,
                }}
                spaceBetween={16}
                breakpoints={{
                  0: { slidesPerView: isLoading ? 2 : Math.min(2, offers.length) },
                  640: { slidesPerView: isLoading ? 2 : Math.min(2, offers.length) },
                  1024: { slidesPerView: isLoading ? 4 : Math.min(4, offers.length) },
                }}
                className='min-h-[220px] sm:min-h-[250px]'
              >
                {swiperContent}
              </Swiper>
            </div>
          </section>

          {/* secondary cards (services) (omitted for brevity) */}
          <div className='mt-[-35px] grid grid-cols-2 gap-6 px-4 pb-10 sm:grid-cols-3 md:grid-cols-4 md:px-16'>
            {cards2.map((card, index) => {
               // ... card rendering logic ...
               let cardImg;
               switch (card.img) {
                 case 'inspiration': cardImg = inspiration; break;
                 case 'explore': cardImg = explore; break;
                 case 'travel': cardImg = travel; break;
                 case 'call_center': cardImg = call_center; break;
                 default: cardImg = '';
               }
               return (
                 <div key={index} className='flex flex-col items-center text-center'>
                   <img
                     src={cardImg}
                     alt={card.name}
                     className='mb-2 h-12 w-12 sm:h-16 sm:w-16 md:h-20 md:w-20'
                   />
                   <p className='text-lg font-semibold text-[#495BFF]'>{card.name}</p>
                 </div>
               );
            })}
          </div>
        </div>
      </div>

      {/* Custom bullet CSS (omitted for brevity) */}
      <style>{`
        .custom-bullet {
          display: inline-block;
          width: 10px;
          height: 10px;
          background: #888;
          border-radius: 50%;
          margin: 0 4px;
          cursor: pointer;
        }
        .swiper-pagination-bullet-active.custom-bullet {
          background: #495BFF;
        }
      `}</style>
    </>
  );
};

export default SpecialPicks;